import * as React from 'react';
import { AGE_GROUPS, SCHEDULES } from '../data';
import { Icon } from '../icons.jsx';
import { Badge, Button, Field, Input, Select, Toggle } from '../ui.jsx';

const freshLabel = (availability) => {
  if (availability.state === 'not_provided') return 'Not published';
  if (availability.state === 'paused') return 'Paused';
  if (availability.state === 'stale') return 'Needs confirmation';
  return 'Current';
};

export const WorkspaceHeader = ({
  workspace,
  onBack,
  onSignOut,
}) => (
  <header className="live-workspace-header">
    <div className="container live-workspace-header-row">
      <div>
        <button type="button" onClick={onBack}>
          <Icon name="chev-left" size={15}/> Provider account
        </button>
        <Badge tone="brand">Production provider workspace</Badge>
        <h1>{workspace.provider.facilityName}</h1>
        <p>
          Official ID {workspace.provider.id}. Official identity stays read-only;
          the fields below are provider supplied.
        </p>
      </div>
      <Button variant="ghost" onClick={onSignOut}>Sign out</Button>
    </div>
  </header>
);

export const FreshnessPanel = ({
  availability,
  onConfirm,
  confirming,
}) => (
  <section className="live-freshness-panel">
    <div>
      <span className={`live-freshness-indicator is-${availability.state}`}/>
      <div>
        <small>Public availability</small>
        <strong>{freshLabel(availability)}</strong>
      </div>
    </div>
    <dl>
      <div>
        <dt>Version</dt>
        <dd>{availability.version || 'Not published'}</dd>
      </div>
      <div>
        <dt>Last confirmed</dt>
        <dd>
          {availability.lastConfirmedAt
            ? new Date(availability.lastConfirmedAt).toLocaleString()
            : 'Not yet'}
        </dd>
      </div>
      <div>
        <dt>Refresh by</dt>
        <dd>
          {availability.staleAfter
            ? new Date(availability.staleAfter).toLocaleString()
            : 'Publish first'}
        </dd>
      </div>
    </dl>
    <Button
      variant="secondary"
      disabled={
        availability.status !== 'live' ||
        availability.version < 1 ||
        confirming
      }
      onClick={onConfirm}
    >
      {confirming ? 'Confirming…' : 'Confirm still current'}
    </Button>
  </section>
);

export const ProfileBasicsEditor = ({ profile, onChange }) => (
  <section className="live-workspace-panel">
    <div className="live-workspace-panel-heading">
      <div>
        <span>Provider supplied</span>
        <h2>Family-facing basics</h2>
      </div>
      <Badge tone="neutral">Required</Badge>
    </div>
    <div className="live-profile-grid">
      <Field label="Regular hours">
        <Input
          value={profile.hours}
          onChange={(event) => onChange('hours', event.target.value)}
          placeholder="Mon-Fri, 7:30 AM-5:30 PM"
        />
      </Field>
      <Field label="Languages" hint="Separate languages with commas.">
        <Input
          value={profile.languages}
          onChange={(event) => onChange('languages', event.target.value)}
          placeholder="English, Spanish"
        />
      </Field>
      <Field label="Curriculum summary" style={{ gridColumn: '1 / -1' }}>
        <textarea
          rows={4}
          value={profile.curriculumSummary}
          onChange={(event) =>
            onChange('curriculumSummary', event.target.value)
          }
          placeholder="Describe the learning approach families should understand."
        />
      </Field>
      <div className="live-tour-options">
        <Toggle
          value={profile.acceptsInPersonTours}
          onChange={(value) => onChange('acceptsInPersonTours', value)}
          label="In-person tours"
        />
        <Toggle
          value={profile.acceptsVirtualTours}
          onChange={(value) => onChange('acceptsVirtualTours', value)}
          label="Virtual tours"
        />
      </div>
    </div>
  </section>
);

const ClassroomEditor = ({ classroom, onChange, onRemove }) => (
  <article className="live-classroom-editor">
    <div className="live-classroom-editor-heading">
      <div>
        <Icon name="home" size={17}/>
        <strong>{classroom.name || 'New classroom'}</strong>
      </div>
      <button type="button" onClick={onRemove} aria-label="Remove classroom">
        <Icon name="x" size={15}/>
      </button>
    </div>
    <div className="live-classroom-grid">
      <Field label="Classroom name">
        <Input
          value={classroom.name}
          onChange={(event) => onChange('name', event.target.value)}
          placeholder="Sunbeam Room"
        />
      </Field>
      <Field label="Age group">
        <Select
          value={classroom.ageGroup}
          onChange={(value) => onChange('ageGroup', value)}
          options={AGE_GROUPS}
        />
      </Field>
      <Field label="Schedule">
        <Select
          value={classroom.schedule}
          onChange={(value) => onChange('schedule', value)}
          options={SCHEDULES}
        />
      </Field>
      <Field label="Capacity">
        <Input
          type="number"
          min="1"
          value={classroom.capacity}
          onChange={(event) => onChange('capacity', Number(event.target.value))}
        />
      </Field>
      <Field label="Currently enrolled">
        <Input
          type="number"
          min="0"
          value={classroom.enrolled}
          onChange={(event) => onChange('enrolled', Number(event.target.value))}
        />
      </Field>
      <div className="live-opening-total">
        <span>Calculated openings</span>
        <strong>{Math.max(0, classroom.capacity - classroom.enrolled)}</strong>
      </div>
      <Field label="Opening date">
        <Input
          type="date"
          value={classroom.openingDate || ''}
          onChange={(event) => onChange('openingDate', event.target.value)}
        />
      </Field>
      <Field label="Families waiting">
        <Input
          type="number"
          min="0"
          value={classroom.waitlistCount}
          onChange={(event) =>
            onChange('waitlistCount', Number(event.target.value))
          }
        />
      </Field>
    </div>
  </article>
);

export const ClassroomEditors = ({
  classrooms,
  onChange,
  onRemove,
  onAdd,
}) => (
  <section className="live-workspace-panel">
    <div className="live-workspace-panel-heading">
      <div>
        <span>Operational availability</span>
        <h2>Classrooms</h2>
      </div>
      <Button size="sm" icon="plus" onClick={onAdd}>Add classroom</Button>
    </div>
    <div className="live-classroom-list">
      {classrooms.map((classroom, index) => (
        <ClassroomEditor
          key={classroom.id || classroom.localId}
          classroom={classroom}
          onChange={(field, value) => onChange(index, field, value)}
          onRemove={() => onRemove(index)}
        />
      ))}
    </div>
  </section>
);

export const PublishBar = ({
  status,
  onStatusChange,
  onPublish,
  publishing,
  totalOpenings,
}) => (
  <div className="live-publish-bar">
    <div>
      <small>Proposed public state</small>
      <strong>
        {status === 'live'
          ? `${totalOpenings} current ${totalOpenings === 1 ? 'opening' : 'openings'}`
          : 'Availability paused'}
      </strong>
    </div>
    <div>
      <Toggle
        value={status === 'live'}
        onChange={(live) => onStatusChange(live ? 'live' : 'paused')}
        label={status === 'live' ? 'Live' : 'Paused'}
      />
      <Button disabled={publishing} onClick={onPublish}>
        {publishing ? 'Publishing…' : 'Review and publish'}
      </Button>
    </div>
  </div>
);
